import { useEffect, useMemo, useRef, useState } from "react";
import { AsyncActionButton, CheckboxField, Modal, Panel, ResourceTable, SelectField, SimpleForm, TextField } from "./ui.jsx";

const roleOptions = [
  { value: "sales", label: "Sales Executive" },
  { value: "manager", label: "Area Manager" },
  { value: "support", label: "Support" },
  { value: "admin", label: "Administrator" },
];

const defaultDesignation = {
  sales: "Sales Executive",
  manager: "Area Manager",
  support: "Customer Support",
  admin: "Administrator",
};

const emptyMember = {
  name: "",
  username: "",
  email: "",
  mobile: "",
  role: "sales",
  designation: defaultDesignation.sales,
  teamId: "",
  territory: "",
  assignAsTeamManager: false,
  initialPassword: "",
};

export function AdminOrganization({
  teams,
  users,
  currentUser,
  dropdownOptions = [],
  passwordRequired = false,
  focusEmployeeId = "",
  onEmployeeFocusHandled,
  onCreate,
  onUpdate,
}) {
  const firstTeamId = teams.find((team) => team.id === currentUser.teamId)?.id || teams[0]?.id || "";
  const [memberForm, setMemberForm] = useState({ ...emptyMember, teamId: firstTeamId });
  const [teamForm, setTeamForm] = useState({ name: "", parentTeamId: firstTeamId, managerId: "" });
  const [memberEditor, setMemberEditor] = useState(null);
  const [teamEditor, setTeamEditor] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [designationEditor, setDesignationEditor] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const memberPanelRef = useRef(null);
  const teamPanelRef = useRef(null);
  const peoplePanelRef = useRef(null);
  const focusedMember = focusEmployeeId ? users.find((item) => item.id === focusEmployeeId) : null;

  useEffect(() => {
    if (!firstTeamId) return;
    setMemberForm((current) => current.teamId ? current : { ...current, teamId: firstTeamId });
    setTeamForm((current) => current.parentTeamId ? current : { ...current, parentTeamId: firstTeamId });
  }, [firstTeamId]);

  useEffect(() => {
    if (!focusedMember) return;
    setSearch(focusedMember.name || focusedMember.username || "");
    setRoleFilter(focusedMember.role || "all");
    setTeamFilter(teams.some((team) => team.id === focusedMember.teamId) ? focusedMember.teamId : "all");
    openMemberEditor(focusedMember);
    focusPanel(peoplePanelRef, "input");
  }, [focusedMember?.id, focusedMember?.name, focusedMember?.username, focusedMember?.role, focusedMember?.teamId, teams.some((team) => team.id === focusedMember?.teamId)]);

  const teamOptions = teams.map((team) => ({ value: team.id, label: team.name }));
  const managerOptions = [
    { value: "", label: "No manager assigned" },
    ...users
      .filter((member) => member.active !== false && ["manager", "admin", "support"].includes(member.role))
      .map((member) => ({ value: member.id, label: `${member.name} - ${roleLabel(member.role)}` })),
  ];
  const designationSuggestions = useMemo(() => [...new Set([
    ...Object.values(defaultDesignation),
    ...dropdownOptions
      .filter((option) => option.category === "designation" && option.active !== false)
      .map((option) => option.value || option.label),
    ...users.map((member) => member.designation),
  ].filter(Boolean))].sort((left, right) => left.localeCompare(right)), [dropdownOptions, users]);
  const teamName = (teamId) => teams.find((team) => team.id === teamId)?.name || "Unassigned";
  const userName = (userId) => users.find((member) => member.id === userId)?.name || "Not assigned";
  const memberNameLink = (member) => (
    <button className="table-link-btn" type="button" onClick={() => openMemberEditor(member)}>
      {member.name || "Unnamed employee"}
    </button>
  );
  const managerNameLink = (managerId) => {
    if (!managerId) return "Not assigned";
    const manager = users.find((member) => member.id === managerId);
    if (!manager) return userName(managerId);
    return (
      <button className="table-link-btn" type="button" onClick={() => openMemberEditor(manager)}>
        {manager.name}
      </button>
    );
  };
  const visibleMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((member) => {
      if (roleFilter !== "all" && member.role !== roleFilter) return false;
      if (teamFilter !== "all" && member.teamId !== teamFilter) return false;
      if (!query) return true;
      return [member.name, member.email, member.username, member.designation, member.territory, teamName(member.teamId)]
        .some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [users, teams, search, roleFilter, teamFilter]);

  function changeMemberRole(role, target = "create") {
    const setter = target === "create" ? setMemberForm : setMemberEditor;
    setter((current) => ({
      ...current,
      role,
      designation: !current.designation || Object.values(defaultDesignation).includes(current.designation)
        ? defaultDesignation[role]
        : current.designation,
      assignAsTeamManager: role === "manager" ? current.assignAsTeamManager : false,
    }));
  }

  function openMemberEditor(member, mode = "edit") {
    const fullMember = users.find((item) => item.id === member.id) || {};
    const sourceMember = { ...member, ...fullMember };
    const managedTeamId = teams.find((team) => team.managerId === sourceMember.id)?.id || "";
    setMemberEditor({
      ...sourceMember,
      email: sourceMember.email || "",
      mobile: sourceMember.mobile || "",
      teamId: managedTeamId || sourceMember.teamId || firstTeamId,
      designation: sourceMember.designation || defaultDesignation[sourceMember.role] || "",
      assignAsTeamManager: Boolean(managedTeamId) || teams.some((team) => team.managerId === sourceMember.id && team.id === sourceMember.teamId),
      directReportIds: [],
      reportCandidateId: "",
      reportSearch: "",
      newPassword: "",
      mode,
    });
  }

  function focusPanel(ref, selector) {
    window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      ref.current?.querySelector(selector)?.focus({ preventScroll: true });
    }, 0);
  }

  function beginManagerOnboarding(returnTo = "team-create") {
    const returningEditor = returnTo === "team-editor" ? teamEditor : null;
    const targetTeamId = returningEditor?.id || teamForm.parentTeamId || firstTeamId;
    setWorkflow({
      kind: "manager",
      returnTo,
      memberDraft: memberForm,
      teamEditorDraft: returningEditor,
    });
    if (returningEditor) setTeamEditor(null);
    setMemberForm({
      ...emptyMember,
      role: "manager",
      designation: defaultDesignation.manager,
      teamId: targetTeamId,
      assignAsTeamManager: Boolean(returningEditor),
    });
    focusPanel(memberPanelRef, "input");
  }

  function beginTeamCreation(returnTo = "member-create") {
    const returningEditor = returnTo === "member-editor" ? memberEditor : null;
    setWorkflow({
      kind: "team",
      returnTo,
      memberDraft: memberForm,
      memberEditorDraft: returningEditor,
    });
    if (returningEditor) setMemberEditor(null);
    focusPanel(teamPanelRef, "input");
  }

  function beginParentTeamCreation(returnTo = "team-create") {
    const returningEditor = returnTo === "team-editor" ? teamEditor : null;
    setWorkflow({
      kind: "parent-team",
      returnTo,
      teamDraft: teamForm,
      teamEditorDraft: returningEditor,
    });
    if (returningEditor) setTeamEditor(null);
    setTeamForm({ name: "", parentTeamId: safeParentTeamId(returningEditor?.id), managerId: "" });
    focusPanel(teamPanelRef, "input");
  }

  function beginReportOnboarding(managerDraft) {
    setWorkflow({
      kind: "report-onboard",
      memberDraft: memberForm,
      managerEditorDraft: managerDraft,
    });
    setMemberEditor(null);
    setMemberForm({
      ...emptyMember,
      role: "sales",
      designation: defaultDesignation.sales,
      teamId: managerDraft.teamId || firstTeamId,
      territory: managerDraft.territory || "",
      assignAsTeamManager: false,
    });
    focusPanel(memberPanelRef, "input");
  }

  function beginReportEdit(report, managerDraft) {
    setWorkflow({ kind: "report-edit", managerEditorDraft: managerDraft });
    openMemberEditor(report);
  }

  function openTeamEditorById(teamId) {
    const team = teams.find((item) => item.id === teamId);
    if (team) setTeamEditor({ ...team });
  }

  function openMemberEditorById(memberId) {
    const member = users.find((item) => item.id === memberId);
    if (member) openMemberEditor(member);
  }

  function beginTeamEditFromMemberEditor(teamId) {
    const team = teams.find((item) => item.id === teamId);
    if (!team || !memberEditor) return;
    setWorkflow({ kind: "member-team-edit", memberEditorDraft: memberEditor });
    setMemberEditor(null);
    setTeamEditor({ ...team });
  }

  function beginManagerEditFromTeamEditor() {
    if (!teamEditor?.managerId) return;
    const manager = users.find((item) => item.id === teamEditor.managerId);
    if (!manager) return;
    setWorkflow({ kind: "team-manager-edit", teamEditorDraft: teamEditor });
    setTeamEditor(null);
    openMemberEditor(manager);
  }

  function returnToManagerEditor(nextManagerDraft = workflow?.managerEditorDraft) {
    setTeamEditor(null);
    setMemberEditor(nextManagerDraft ? { ...nextManagerDraft } : null);
    setWorkflow(null);
  }

  function returnToTeamEditor(nextTeamDraft = workflow?.teamEditorDraft) {
    setMemberEditor(null);
    setTeamEditor(nextTeamDraft ? { ...nextTeamDraft } : null);
    setWorkflow(null);
  }

  function returnToMemberEditor(nextMemberDraft = workflow?.memberEditorDraft) {
    setTeamEditor(null);
    setMemberEditor(nextMemberDraft ? { ...nextMemberDraft } : null);
    setWorkflow(null);
  }

  async function saveMember() {
    const saved = await onCreate("/users", memberForm);
    if (saved === false) throw new Error("Save failed");
    if (workflow?.kind === "manager") {
      if (workflow.returnTo === "team-editor") {
        setTeamEditor({ ...workflow.teamEditorDraft, managerId: saved.id });
      } else {
        setTeamForm((current) => ({ ...current, managerId: saved.id }));
        focusPanel(teamPanelRef, "input");
      }
      setMemberForm(workflow.memberDraft);
      setWorkflow(null);
      return;
    }
    if (workflow?.kind === "report-onboard") {
      setMemberForm(workflow.memberDraft);
      returnToManagerEditor();
      return;
    }
    setMemberForm({ ...emptyMember, teamId: memberForm.teamId || firstTeamId });
  }

  async function saveTeam() {
    const saved = await onCreate("/teams", teamForm);
    if (saved === false) throw new Error("Save failed");
    if (workflow?.kind === "team") {
      if (workflow.returnTo === "member-editor") {
        setMemberEditor({ ...workflow.memberEditorDraft, teamId: saved.id });
      } else {
        setMemberForm({ ...workflow.memberDraft, teamId: saved.id });
        focusPanel(memberPanelRef, "input");
      }
      setWorkflow(null);
      setTeamForm({ name: "", parentTeamId: teamForm.parentTeamId || firstTeamId, managerId: "" });
      return;
    }
    if (workflow?.kind === "parent-team") {
      if (workflow.returnTo === "team-editor") {
        setTeamEditor({ ...workflow.teamEditorDraft, parentTeamId: saved.id });
        setTeamForm(workflow.teamDraft);
      } else {
        setTeamForm({ ...workflow.teamDraft, parentTeamId: saved.id });
      }
      setWorkflow(null);
      return;
    }
    setTeamForm({ name: "", parentTeamId: teamForm.parentTeamId || firstTeamId, managerId: "" });
  }

  async function saveDesignation() {
    const value = designationEditor.value.trim();
    const saved = await onCreate("/dropdown-options", {
      category: "designation",
      label: value,
      value,
      sortOrder: (designationSuggestions.length + 1) * 10,
      active: true,
    });
    if (saved === false) throw new Error("Save failed");
    const designation = saved.value || value;
    if (designationEditor.target === "editor") {
      setMemberEditor({ ...designationEditor.memberEditorDraft, designation });
    } else {
      setMemberForm((current) => ({ ...current, designation }));
    }
    setDesignationEditor(null);
  }

  function openDesignationEditor(target) {
    const editor = target === "editor" ? memberEditor : null;
    setDesignationEditor({ target, value: "", memberEditorDraft: editor });
    if (editor) setMemberEditor(null);
  }

  function closeDesignationEditor() {
    if (designationEditor.target === "editor") setMemberEditor(designationEditor.memberEditorDraft);
    setDesignationEditor(null);
  }

  function workflowBackLabel() {
    if (workflow?.kind === "manager") return workflow.returnTo === "team-editor" ? "Back to team form" : "Back to create team form";
    if (workflow?.kind === "report-onboard") return "Back to manager form";
    if (workflow?.kind === "team") return workflow.returnTo === "member-editor" ? "Back to employee form" : "Back to onboard employee";
    if (workflow?.kind === "parent-team") return workflow.returnTo === "team-editor" ? "Back to team form" : "Back to create team form";
    if (workflow?.kind === "report-edit") return "Back to manager form";
    if (workflow?.kind === "team-manager-edit") return "Back to team form";
    if (workflow?.kind === "member-team-edit") return "Back to employee form";
    return "Back to previous form";
  }

  function goBackFromWorkflow() {
    if (!workflow) return;
    if (workflow.kind === "manager") {
      setMemberForm(workflow.memberDraft);
      if (workflow.returnTo === "team-editor") {
        returnToTeamEditor();
      } else {
        setWorkflow(null);
        focusPanel(teamPanelRef, "input");
      }
      return;
    }
    if (workflow.kind === "report-onboard") {
      setMemberForm(workflow.memberDraft);
      returnToManagerEditor();
      return;
    }
    if (workflow.kind === "team") {
      setTeamForm({ name: "", parentTeamId: firstTeamId, managerId: "" });
      if (workflow.returnTo === "member-editor") {
        returnToMemberEditor();
      } else {
        setMemberForm(workflow.memberDraft);
        setWorkflow(null);
        focusPanel(memberPanelRef, "input");
      }
      return;
    }
    if (workflow.kind === "parent-team") {
      setTeamForm(workflow.teamDraft);
      if (workflow.returnTo === "team-editor") {
        returnToTeamEditor();
      } else {
        setWorkflow(null);
        focusPanel(teamPanelRef, "input");
      }
      return;
    }
    if (workflow.kind === "report-edit" || workflow.kind === "team-manager-edit") {
      closeMemberEditor();
      return;
    }
    if (workflow.kind === "member-team-edit") {
      closeTeamEditor();
    }
  }

  function organizationFlowNotice() {
    if (workflow?.kind === "manager") return "Add the new team manager here. After saving, you will return to the team form with this person selected.";
    if (workflow?.kind === "report-onboard") return "Add the new employee here. After saving, you will return to the manager editor.";
    return "";
  }

  function teamFlowNotice() {
    if (workflow?.kind === "team") return "Create the missing team here. After saving, you will return to the employee form with the new team selected.";
    if (workflow?.kind === "parent-team") return "Create the parent team here. After saving, you will return to the original team form with this reporting line selected.";
    return "";
  }

  function safeParentTeamId(editedTeamId = "") {
    if (!editedTeamId) return firstTeamId;
    const blocked = new Set([editedTeamId, ...descendantTeamIds(teams, editedTeamId)]);
    return teams.find((team) => !blocked.has(team.id))?.id || "";
  }

  function confirmUserStatusChange(member) {
    const willReactivate = member.active === false;
    const action = willReactivate ? "Reactivate" : "Deactivate";
    const consequence = willReactivate
      ? "They will be able to sign in and receive assignments again."
      : "They will be signed out of the employee app and removed from new assignments. Existing history remains available.";
    if (!window.confirm(`${action} ${member.name}? ${consequence}`)) return true;
    return onUpdate(`/users/${member.id}`, { active: willReactivate });
  }

  async function saveMemberEditor() {
    const payload = memberEditor.mode === "move"
      ? {
          teamId: memberEditor.teamId,
          territory: memberEditor.territory,
          assignAsTeamManager: memberEditor.role === "manager" && memberEditor.assignAsTeamManager,
        }
        : {
          name: memberEditor.name,
          mobile: memberEditor.mobile,
          ...(memberEditor.email ? { email: memberEditor.email } : {}),
          role: memberEditor.role,
          designation: memberEditor.designation,
          ...(memberEditor.newPassword ? { newPassword: memberEditor.newPassword } : {}),
          teamId: memberEditor.teamId,
          territory: memberEditor.territory,
          assignAsTeamManager: ["manager", "admin", "support"].includes(memberEditor.role) && memberEditor.assignAsTeamManager,
          directReportIds: memberEditor.role === "manager" ? memberEditor.directReportIds || [] : undefined,
        };
    const saved = await onUpdate(`/users/${memberEditor.id}`, payload);
    if (saved === false) throw new Error("Save failed");
    if (workflow?.kind === "report-edit") {
      returnToManagerEditor();
      return;
    }
    if (workflow?.kind === "team-manager-edit") {
      returnToTeamEditor({ ...workflow.teamEditorDraft, managerId: saved.id || memberEditor.id });
      return;
    }
    setMemberEditor(null);
    if (focusEmployeeId === memberEditor.id) onEmployeeFocusHandled?.();
  }

  function closeMemberEditor() {
    const closedMemberId = memberEditor?.id;
    if (workflow?.kind === "report-edit") {
      returnToManagerEditor();
      return;
    }
    if (workflow?.kind === "team-manager-edit") {
      returnToTeamEditor();
      return;
    }
    setMemberEditor(null);
    if (focusEmployeeId === closedMemberId) onEmployeeFocusHandled?.();
  }

  async function saveTeamEditor() {
    const saved = await onUpdate(`/teams/${teamEditor.id}`, {
      name: teamEditor.name,
      parentTeamId: teamEditor.parentTeamId,
      managerId: teamEditor.managerId,
    });
    if (saved === false) throw new Error("Save failed");
    if (workflow?.kind === "member-team-edit") {
      returnToMemberEditor();
      return;
    }
    setTeamEditor(null);
  }

  function closeTeamEditor() {
    if (workflow?.kind === "member-team-edit") {
      returnToMemberEditor();
      return;
    }
    setTeamEditor(null);
  }

  return (
    <section className="organization-management" aria-labelledby="organization-management-title">
      <div className="section-heading">
        <div>
          <h2 id="organization-management-title">Organization Management</h2>
          <p className="subtle">Create teams, onboard employees and area managers, edit designations, and move people between reporting teams.</p>
        </div>
      </div>

      <div className="organization-flow-nav" aria-label="Organization setup shortcuts">
        <button type="button" onClick={() => focusPanel(memberPanelRef, "input")}><span>1</span> Onboard</button>
        <button type="button" onClick={() => focusPanel(teamPanelRef, "input")}><span>2</span> Create Team</button>
        <button type="button" onClick={() => focusPanel(peoplePanelRef, "input")}><span>3</span> Manage People</button>
      </div>

      <div className="organization-quick-grid">
        <div ref={memberPanelRef} className="workflow-target">
        <Panel title="Onboard Employee" subtitle="Choose a security access role, then use a standard or custom business designation.">
          {organizationFlowNotice() ? (
            <FlowNotice message={organizationFlowNotice()} backLabel={workflowBackLabel()} onBack={goBackFromWorkflow} />
          ) : null}
          <SimpleForm
            submitLabel="Onboard Employee"
            submittingLabel="Adding..."
            disabled={!teamOptions.length}
            onSubmit={saveMember}
          >
            <TextField label="Full name" value={memberForm.name} required maxLength={140} onChange={(name) => setMemberForm({ ...memberForm, name })} />
            <TextField label="Username" value={memberForm.username} required maxLength={80} onChange={(username) => setMemberForm({ ...memberForm, username })} />
            <TextField label="Work email" type="email" value={memberForm.email} required maxLength={180} onChange={(email) => setMemberForm({ ...memberForm, email })} />
            <TextField label="Mobile number (with country code)" type="tel" value={memberForm.mobile || ""} maxLength={30} onChange={(mobile) => setMemberForm({ ...memberForm, mobile })} />
            {passwordRequired ? (
              <TextField
                label="Initial password"
                type="password"
                value={memberForm.initialPassword}
                required
                maxLength={128}
                onChange={(initialPassword) => setMemberForm({ ...memberForm, initialPassword })}
              />
            ) : null}
            <SelectField label="Access role" value={memberForm.role} options={roleOptions} onChange={(role) => changeMemberRole(role)} />
            <TextField
              label="Designation"
              value={memberForm.designation}
              maxLength={100}
              suggestions={designationSuggestions}
              actionLabel="Add another job title"
              onAction={() => openDesignationEditor("create")}
              onChange={(designation) => setMemberForm({ ...memberForm, designation })}
            />
            <SelectField
              label="Team"
              value={memberForm.teamId || firstTeamId}
              options={teamOptions}
              actionLabel="Create a new team"
              onAction={() => beginTeamCreation("member-create")}
              secondaryActionLabel="Edit selected team"
              secondaryActionDisabled={!memberForm.teamId}
              onSecondaryAction={() => openTeamEditorById(memberForm.teamId)}
              onChange={(teamId) => setMemberForm({ ...memberForm, teamId })}
            />
            <TextField label="Territory / area" value={memberForm.territory} maxLength={140} onChange={(territory) => setMemberForm({ ...memberForm, territory })} />
            {memberForm.role === "manager" ? (
              <CheckboxField label="Make this person the manager of the selected team" checked={memberForm.assignAsTeamManager} onChange={(assignAsTeamManager) => setMemberForm({ ...memberForm, assignAsTeamManager })} />
            ) : null}
          </SimpleForm>
        </Panel>
        </div>

      <div ref={teamPanelRef} className="workflow-target">
        <Panel title="Create Team" subtitle="Add a team under an existing reporting line and optionally assign its manager.">
          {teamFlowNotice() ? (
            <FlowNotice message={teamFlowNotice()} backLabel={workflowBackLabel()} onBack={goBackFromWorkflow} />
          ) : null}
          <SimpleForm
            submitLabel="Create Team"
            submittingLabel="Creating..."
            disabled={!teamOptions.length}
            onSubmit={saveTeam}
          >
            <TextField label="Team name" value={teamForm.name} required maxLength={140} onChange={(name) => setTeamForm({ ...teamForm, name })} />
            <SelectField
              label="Reports to"
              value={teamForm.parentTeamId || firstTeamId}
              options={teamOptions}
              actionLabel="Create parent team"
              onAction={() => beginParentTeamCreation("team-create")}
              secondaryActionLabel="Edit selected parent team"
              secondaryActionDisabled={!teamForm.parentTeamId && !firstTeamId}
              onSecondaryAction={() => openTeamEditorById(teamForm.parentTeamId || firstTeamId)}
              onChange={(parentTeamId) => setTeamForm({ ...teamForm, parentTeamId })}
            />
            <SelectField
              label="Team manager"
              value={teamForm.managerId}
              options={managerOptions}
              actionLabel="Onboard a new team manager"
              onAction={() => beginManagerOnboarding("team-create")}
              secondaryActionLabel="Open selected manager"
              secondaryActionDisabled={!teamForm.managerId}
              onSecondaryAction={() => openMemberEditorById(teamForm.managerId)}
              onChange={(managerId) => setTeamForm({ ...teamForm, managerId })}
            />
          </SimpleForm>
        </Panel>
        </div>
      </div>

      <div ref={peoplePanelRef} className="workflow-target">
      <Panel title="People Directory" subtitle="Search, edit, move, activate, or deactivate employees and managers.">
        <div className="organization-filters">
          <TextField label="Search people" value={search} maxLength={140} onChange={setSearch} />
          <SelectField label="Role" value={roleFilter} options={[{ value: "all", label: "All roles" }, ...roleOptions]} onChange={setRoleFilter} />
          <SelectField
            label="Team"
            value={teamFilter}
            options={[{ value: "all", label: "All teams" }, ...teamOptions]}
            secondaryActionLabel="Edit selected team"
            secondaryActionDisabled={teamFilter === "all"}
            onSecondaryAction={() => openTeamEditorById(teamFilter)}
            onChange={setTeamFilter}
          />
        </div>
        <ResourceTable
          rows={visibleMembers}
          columns={[
            ["name", "Name", (_value, row) => memberNameLink(row)],
            ["designation", "Designation", (value, row) => value || roleLabel(row.role)],
            ["role", "Access", roleLabel],
            ["teamId", "Team", teamName],
            ["territory", "Territory"],
            ["active", "Status", (value) => value === false ? "Inactive" : "Active"],
          ]}
          action={(member) => (
            <div className="row-actions">
              <button className="mini-btn" type="button" onClick={() => openMemberEditor(member)}>Edit</button>
              <button className="mini-btn" type="button" onClick={() => openMemberEditor(member, "move")}>Move</button>
              {member.id !== currentUser.id ? (
                <AsyncActionButton
                  pendingLabel={member.active === false ? "Reactivating..." : "Deactivating..."}
                  errorMessage="We could not update this employee. Please try again."
                  onAction={() => confirmUserStatusChange(member)}
                >
                  {member.active === false ? "Reactivate" : "Deactivate"}
                </AsyncActionButton>
              ) : null}
            </div>
          )}
        />
      </Panel>
      </div>

      <Panel title="Team Directory" subtitle="Edit team names, reporting lines, and area-manager ownership.">
        <ResourceTable
          rows={teams}
          columns={[
            ["name", "Team"],
            ["parentTeamId", "Reports To", (value) => value ? teamName(value) : "Top level"],
            ["managerId", "Manager", managerNameLink],
            ["id", "Members", (value) => users.filter((member) => member.teamId === value && member.active !== false).length],
          ]}
          action={(team) => <button className="mini-btn" type="button" onClick={() => setTeamEditor({ ...team })}>Edit Team</button>}
        />
      </Panel>

      {memberEditor ? (
        <MemberEditor
          member={memberEditor}
          setMember={setMemberEditor}
          teamOptions={teamOptions}
          designationSuggestions={designationSuggestions}
          users={users}
          passwordRequired={passwordRequired}
          teamName={teamName}
          onRoleChange={(role) => changeMemberRole(role, "edit")}
          onAddDesignation={() => openDesignationEditor("editor")}
          onAddTeam={() => beginTeamCreation("member-editor")}
          onEditTeam={beginTeamEditFromMemberEditor}
          canEditTeam={!workflow}
          onOnboardReport={beginReportOnboarding}
          onEditReport={beginReportEdit}
          backLabel={workflow?.kind === "report-edit" || workflow?.kind === "team-manager-edit" ? workflowBackLabel() : ""}
          onBack={workflow?.kind === "report-edit" || workflow?.kind === "team-manager-edit" ? goBackFromWorkflow : undefined}
          onSave={saveMemberEditor}
          onClose={closeMemberEditor}
        />
      ) : null}
      {teamEditor ? (
        <TeamEditor
          team={teamEditor}
          setTeam={setTeamEditor}
          parentOptions={teamParentOptions(teams, teamEditor.id)}
          managerOptions={managerOptions}
          onAddParentTeam={() => beginParentTeamCreation("team-editor")}
          onAddManager={() => beginManagerOnboarding("team-editor")}
          onEditManager={beginManagerEditFromTeamEditor}
          backLabel={workflow?.kind === "member-team-edit" ? workflowBackLabel() : ""}
          onBack={workflow?.kind === "member-team-edit" ? goBackFromWorkflow : undefined}
          onSave={saveTeamEditor}
          onClose={closeTeamEditor}
        />
      ) : null}
      {designationEditor ? (
        <Modal
          label="Add custom designation form"
          onBack={closeDesignationEditor}
          backLabel={designationEditor.target === "editor" ? "Back to employee form" : "Back to onboard employee"}
        >
          <div className="panel-header">
            <div>
              <h2>Add Custom Designation</h2>
              <p className="subtle">Save this job title once, then reuse it while onboarding or editing employees.</p>
            </div>
          </div>
          <SimpleForm onSubmit={saveDesignation} submitLabel="Add Designation" submittingLabel="Adding...">
            <TextField
              label="Designation name"
              value={designationEditor.value}
              required
              maxLength={100}
              suggestions={designationSuggestions}
              onChange={(value) => setDesignationEditor({ ...designationEditor, value })}
            />
            <button className="ghost-btn full" type="button" onClick={closeDesignationEditor}>Cancel</button>
          </SimpleForm>
        </Modal>
      ) : null}
    </section>
  );
}

function FlowNotice({ message, backLabel, onBack }) {
  return (
    <div className="flow-notice">
      <span>{message}</span>
      <button className="flow-back-btn" type="button" aria-label="Back to previous form" title={backLabel || "Back to previous form"} onClick={onBack}>
        <span aria-hidden="true">{"<"}</span>
        Back
      </button>
    </div>
  );
}

function MemberEditor({
  member,
  setMember,
  teamOptions,
  designationSuggestions,
  users,
  passwordRequired = false,
  teamName,
  onRoleChange,
  onAddDesignation,
  onAddTeam,
  onEditTeam,
  canEditTeam = true,
  onOnboardReport,
  onEditReport,
  onBack,
  backLabel,
  onSave,
  onClose,
}) {
  const moving = member.mode === "move";
  const canManageReports = !moving && member.role === "manager";
  const currentReports = canManageReports
    ? users.filter((item) => item.id !== member.id && item.teamId === member.teamId && item.role !== "admin")
    : [];
  const queuedReportIds = new Set(member.directReportIds || []);
  const queuedReports = users.filter((item) => queuedReportIds.has(item.id));
  const allReportCandidates = canManageReports
    ? users.filter((item) =>
        item.id !== member.id &&
        item.active !== false &&
        ["sales", "support"].includes(item.role) &&
        item.teamId !== member.teamId &&
        !queuedReportIds.has(item.id)
      )
    : [];
  const reportSearch = String(member.reportSearch || "").trim().toLowerCase();
  const reportCandidates = reportSearch
    ? allReportCandidates.filter((item) =>
        [item.name, item.designation, roleLabel(item.role), item.territory, teamName(item.teamId)]
          .some((value) => String(value || "").toLowerCase().includes(reportSearch))
      )
    : allReportCandidates;
  const reportCandidateOptions = [
    {
      value: "",
      label: !allReportCandidates.length
        ? "No movable employees available"
        : reportCandidates.length
          ? "Choose employee to move"
          : "No matching employees",
    },
    ...reportCandidates.map((item) => ({ value: item.id, label: `${item.name} - ${roleLabel(item.role)} - ${teamName(item.teamId)}` })),
  ];
  const selectedReportCandidateId = reportCandidates.some((item) => item.id === member.reportCandidateId)
    ? member.reportCandidateId
    : "";

  function queueReport() {
    if (!selectedReportCandidateId) return;
    setMember({
      ...member,
      directReportIds: [...new Set([...(member.directReportIds || []), selectedReportCandidateId])],
      reportCandidateId: "",
    });
  }

  function removeQueuedReport(memberId) {
    setMember({
      ...member,
      directReportIds: (member.directReportIds || []).filter((item) => item !== memberId),
    });
  }

  return (
    <Modal label={moving ? `Move ${member.name} form` : `Edit ${member.name} form`} onBack={onBack} backLabel={backLabel}>
      <div className="panel-header">
        <div>
          <h2>{moving ? `Move ${member.name}` : `Edit ${member.name}`}</h2>
          <p className="subtle">{moving ? "Choose the new reporting team. Active prospects and pending visits move with this person." : "Update identity, access, designation, and reporting assignment."}</p>
        </div>
      </div>
      <SimpleForm onSubmit={onSave} submitLabel={moving ? "Move Team Member" : "Save Changes"} submittingLabel="Saving...">
        {!moving ? (
          <>
            <TextField label="Full name" value={member.name} required maxLength={140} onChange={(name) => setMember({ ...member, name })} />
            <TextField label="Work email" type="email" value={member.email} required={Boolean(member.email)} maxLength={180} onChange={(email) => setMember({ ...member, email })} />
            <TextField label="Mobile number (with country code)" type="tel" value={member.mobile || ""} maxLength={30} onChange={(mobile) => setMember({ ...member, mobile })} />
            {passwordRequired ? (
              <TextField
                label="Reset password"
                type="password"
                value={member.newPassword || ""}
                maxLength={128}
                onChange={(newPassword) => setMember({ ...member, newPassword })}
              />
            ) : null}
            <SelectField label="Access role" value={member.role} options={roleOptions} onChange={onRoleChange} />
            <TextField label="Designation" value={member.designation} maxLength={100} suggestions={designationSuggestions} actionLabel="Add another job title" onAction={onAddDesignation} onChange={(designation) => setMember({ ...member, designation })} />
          </>
        ) : null}
        <SelectField
          label="Team"
          value={member.teamId}
          options={teamOptions}
          actionLabel="Create a new team"
          onAction={onAddTeam}
          secondaryActionLabel="Edit selected team"
          secondaryActionDisabled={!member.teamId || !canEditTeam}
          onSecondaryAction={() => onEditTeam(member.teamId)}
          onChange={(teamId) => setMember({ ...member, teamId })}
        />
        <TextField label="Territory / area" value={member.territory} maxLength={140} onChange={(territory) => setMember({ ...member, territory })} />
        {member.role === "manager" ? (
          <CheckboxField label="Make this person the manager of the selected team" checked={member.assignAsTeamManager} onChange={(assignAsTeamManager) => setMember({ ...member, assignAsTeamManager })} />
        ) : null}
        {canManageReports ? (
          <div className="manager-report-editor full">
            <div className="manager-report-header">
              <div>
                <h3>Team Members Under This Manager</h3>
                <p className="subtle">Moves apply to active prospects, pending visits, and current targets when you save.</p>
              </div>
              <button className="mini-btn" type="button" onClick={() => onOnboardReport(member)}>+ Onboard New Employee</button>
            </div>
            <div className="manager-report-list" aria-label="Current team members">
              {currentReports.length ? currentReports.map((report) => (
                <div className="manager-report-row" key={report.id}>
                  <div>
                    <strong>{report.name}</strong>
                    <span>{report.designation || roleLabel(report.role)} · {report.territory || "Unassigned"}{report.active === false ? " · Inactive" : ""}</span>
                  </div>
                  <button className="mini-btn" type="button" onClick={() => onEditReport(report, member)}>Edit</button>
                </div>
              )) : <div className="empty compact-empty">No direct team members yet.</div>}
            </div>
            <TextField
              label="Find employee to move"
              value={member.reportSearch || ""}
              maxLength={140}
              onChange={(reportSearch) => setMember({ ...member, reportSearch, reportCandidateId: "" })}
            />
            <SelectField
              label="Move employee under this manager"
              value={selectedReportCandidateId}
              options={reportCandidateOptions}
              disabled={!allReportCandidates.length}
              actionLabel="Move selected employee under this manager"
              actionDisabled={!selectedReportCandidateId}
              onAction={queueReport}
              onChange={(reportCandidateId) => setMember({ ...member, reportCandidateId })}
            />
            {queuedReports.length ? (
              <div className="queued-report-list" aria-label="Team member moves queued for save">
                {queuedReports.map((report) => (
                  <div className="queued-report-chip" key={report.id}>
                    <span>{report.name} from {teamName(report.teamId)}</span>
                    <button type="button" aria-label={`Remove ${report.name} from move list`} onClick={() => removeQueuedReport(report.id)}>x</button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        <button className="ghost-btn full" type="button" onClick={onClose}>Cancel</button>
      </SimpleForm>
    </Modal>
  );
}

function TeamEditor({ team, setTeam, parentOptions, managerOptions, onAddParentTeam, onAddManager, onEditManager, onBack, backLabel, onSave, onClose }) {
  return (
    <Modal label="Edit team form" onBack={onBack} backLabel={backLabel}>
      <div className="panel-header">
        <div>
          <h2>Edit Team</h2>
          <p className="subtle">Rename the team, change its reporting line, or assign an area manager.</p>
        </div>
      </div>
      <SimpleForm onSubmit={onSave} submitLabel="Save Team" submittingLabel="Saving...">
        <TextField label="Team name" value={team.name} required maxLength={140} onChange={(name) => setTeam({ ...team, name })} />
        <SelectField
          label="Reports to"
          value={team.parentTeamId || ""}
          options={parentOptions}
          actionLabel="Create parent team"
          onAction={onAddParentTeam}
          onChange={(parentTeamId) => setTeam({ ...team, parentTeamId })}
        />
        <SelectField
          label="Team manager"
          value={team.managerId || ""}
          options={managerOptions}
          actionLabel="Onboard a new team manager"
          onAction={onAddManager}
          secondaryActionLabel="Open selected manager"
          secondaryActionDisabled={!team.managerId}
          onSecondaryAction={onEditManager}
          onChange={(managerId) => setTeam({ ...team, managerId })}
        />
        <button className="ghost-btn full" type="button" onClick={onClose}>Cancel</button>
      </SimpleForm>
    </Modal>
  );
}

function roleLabel(role) {
  return roleOptions.find((option) => option.value === role)?.label || role || "-";
}

function teamParentOptions(teams, editedTeamId) {
  const blocked = new Set([editedTeamId, ...descendantTeamIds(teams, editedTeamId)]);
  return [
    { value: "", label: "Top level" },
    ...teams.filter((team) => !blocked.has(team.id)).map((team) => ({ value: team.id, label: team.name })),
  ];
}

function descendantTeamIds(teams, rootId) {
  const result = [];
  const queue = [rootId];
  while (queue.length) {
    const parentId = queue.shift();
    for (const team of teams) {
      if (team.parentTeamId === parentId && !result.includes(team.id)) {
        result.push(team.id);
        queue.push(team.id);
      }
    }
  }
  return result;
}
